import { bazaarApi } from '../api/endpoints';
import { StationStore } from '../helperStations/store';
import type { PersonSource } from '../helperStations/sync';
import { intakeDbNames, INTAKE_PREFIX } from './localIntake';
import {
  flushIntake,
  type IntakeFlushOptions,
  type IntakeFlushSummary,
  type IntakeScanApi,
} from './sync';

const runningWithoutLocks = new Set<string>();

/**
 * Sendet die Annahme-Warteschlange unter einer Sperre je Basar (wie `flushStationLocked`): Die Annahme-Seite und der Lauf vor
 * dem Abmelden senden nie gleichzeitig. Hält ein anderer die Sperre, ist es `'busy'` (mit `wait`: warten bis zum Abbruch).
 */
export async function flushIntakeLocked(
  store: StationStore,
  api: IntakeScanApi,
  eventId: string,
  person: PersonSource,
  options: IntakeFlushOptions & { wait?: boolean } = {},
): Promise<IntakeFlushSummary | 'busy'> {
  const locks: LockManager | undefined = 'locks' in navigator ? navigator.locks : undefined;
  if (locks === undefined) {
    if (runningWithoutLocks.has(eventId)) return 'busy';
    runningWithoutLocks.add(eventId);
    try {
      return await flushIntake(store, api, eventId, person, options);
    } finally {
      runningWithoutLocks.delete(eventId);
    }
  }
  const name = `barso-intake-flush-${eventId}`;
  const run = async (lock: Lock | null): Promise<IntakeFlushSummary | 'busy'> =>
    lock === null ? 'busy' : flushIntake(store, api, eventId, person, options);
  if (options.wait === true) {
    return locks.request(name, options.signal === undefined ? {} : { signal: options.signal }, run);
  }
  return locks.request(name, { ifAvailable: true }, run);
}

/**
 * Vor dem Abmelden: Alle wartenden Annahme-Scans dieser Person auf diesem Gerät senden, solange ihr Konto noch angemeldet ist.
 * Was nicht hinausgeht, bleibt liegen und geht nur unter ihrem Konto hinaus. Wirft nie.
 */
export async function flushAllIntakes(
  person: PersonSource,
  api: IntakeScanApi = bazaarApi,
  options: Pick<IntakeFlushOptions, 'signal'> = {},
): Promise<void> {
  if (!navigator.onLine) return;
  for (const name of await intakeDbNames()) {
    if (options.signal?.aborted === true) return;
    const eventId = name.slice(INTAKE_PREFIX.length);
    try {
      const store = await StationStore.open(name);
      try {
        await flushIntakeLocked(store, api, eventId, person, { ...options, wait: true });
      } finally {
        store.close();
      }
    } catch {
      // Speicher gesperrt oder Fehler: die Scans bleiben erhalten
    }
  }
}
